import React, { useState, useEffect, useMemo } from "react";
import "./NewProducts.css";
import ProductCard from "../../components/ProductCard/ProductCard";
import { SkeletonGrid } from "../../components/Skeleton/Skeleton";
import ProductFilters, {
  EMPTY_SELECTION,
  applyFilters,
  countActive,
  useGarmentTypes,
} from "../../components/Filters/ProductFilters";
import { Link, useLocation } from "react-router-dom";

const ProductGrid = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const categoryFilter = params.get("category");
  const colorGroupFilter = params.get("colorGroup");

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await fetch("https://riymay.com/api/v1/user/products?limit=100");
        const data = await response.json();

        let filtered = data.products.filter((p) => p.visibility === "public");

        if (categoryFilter) {
          filtered = filtered.filter(
            (p) => p.category && p.category.toLowerCase() === categoryFilter.toLowerCase()
          );
        }

        if (colorGroupFilter) {
          // A garment can now sit in more than one collection: the rust
          // colourway in Warms, the slate one in Cools. Match if EITHER a
          // per-colour group says so, or the product-level group does.
          //
          // The product-level field is still checked, not replaced: every
          // product uploaded before per-colour groups existed has only that
          // one, and dropping it here would empty these menus.
          const want = colorGroupFilter.toLowerCase();
          filtered = filtered.filter((p) => {
            const perColour = Array.isArray(p.colorGroups)
              && p.colorGroups.some((g) => g && String(g.group || "").toLowerCase() === want);
            const productLevel = p.colorGroup
              && String(p.colorGroup).toLowerCase() === want;
            return perColour || productLevel;
          });
        }

        setProducts(filtered);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryFilter, colorGroupFilter]);

  // Arriving from a collection link is a fresh start; carrying the previous
  // page's colour and size choices across would read as a broken page.
  useEffect(() => {
    setSelection(EMPTY_SELECTION);
  }, [categoryFilter, colorGroupFilter]);

  /* The visible list is a pure function of (products, selection), and
     applyFilters swallows its own errors and hands back every product. There
     is no second copy of the list to fall out of sync, and no way for a filter
     bug to empty the page. */
  // Same fetch the panel uses, so the two can never disagree about which
  // product is in which garment type.
  const { typeByProduct } = useGarmentTypes();

  const visibleProducts = useMemo(
    () => applyFilters(products, selection, typeByProduct),
    [products, selection, typeByProduct]
  );

  const activeCount = countActive(selection);
  const clearFilters = () => setSelection(EMPTY_SELECTION);

  /* ── the scroll-reveal handshake ────────────────────────────────────────
     src/utils/useScrollReveal.js hides cards that are BELOW THE FOLD when it
     first measures them, and un-hides them from a MutationObserver on <body>
     plus a scroll sweep. Filtering adds and removes grid children, so that
     MutationObserver already fires on every change; this is a second, cheaper
     route to the same sweep for the case where a card that was hidden while
     below the fold gets pulled up into view by the products above it going
     away.

     It is a dispatched event — never a timer, never rAF. If this line never
     ran, the MutationObserver would still do the work. No product's visibility
     depends on it. */
  useEffect(() => {
    try {
      window.dispatchEvent(new Event("scroll"));
    } catch (e) {
      /* no Event constructor: the observer covers it */
    }
  }, [visibleProducts]);

  // Shapes of the cards that are coming, rather than a spinner that says nothing.
  if (loading) return <SkeletonGrid count={8} />;

  const headerLabel = categoryFilter || colorGroupFilter;

  return (
    <div>
      {headerLabel && (
        <p style={{ textAlign: "center", padding: "20px 0 0", fontSize: "13px", letterSpacing: "0.2em", textTransform: "uppercase", color: "#666" }}>
          {headerLabel}
        </p>
      )}

      {products.length > 0 && (
        <ProductFilters
          products={products}
          selection={selection}
          onChange={setSelection}
          resultCount={visibleProducts.length}
        />
      )}

      <div className="product-grid">
        {visibleProducts.length > 0 ? (
          visibleProducts.map((product, index) => (
            <Link className="product-card-container" key={product._id} to={`/product/${product._id}`}>
              <ProductCard key={`${product._id}${index}`} product={product} />
            </Link>
          ))
        ) : activeCount > 0 ? (
          /* A combination that matches nothing says so, and offers the way
             back in the same breath. */
          <div className="pg-empty">
            <p className="pg-empty-title">Nothing matches those filters</p>
            <p className="pg-empty-text">
              No piece is available in that combination. Try removing one choice.
            </p>
            <button type="button" className="pg-empty-link pg-empty-button" onClick={clearFilters}>
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="pg-empty">
            <p className="pg-empty-title">Nothing here yet</p>
            <p className="pg-empty-text">
              {colorGroupFilter
                ? `No pieces are tagged ${colorGroupFilter} at the moment.`
                : categoryFilter
                ? `No pieces in ${categoryFilter} at the moment.`
                : "No products available."}
            </p>
            <Link to="/newproduct" className="pg-empty-link">View all products</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductGrid;
